import { useGraphVisualisation } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import DateTimeInput from "@imminently/decisively-ui/DateTimeInput";
import Icon from "@imminently/decisively-ui/Icon";
import { last } from "lodash";
import moment from "moment";
import React, { useState } from "react";
import { GraphStatistics } from "../GraphStatistics/GraphStatistics";
import SearchRuleGraphNodes from "../search/SearchRuleGraphNodes";
import DepthSelector from "./components/DepthSelector";
import GraphTypeSelector from "./components/GraphTypeSelector";
import NodeLimitSelector from "./components/NodeLimitSelector";

import { TogglePaneButton } from "@/common/SplitPane";
import { shownIds } from "../../decisionGraphView";
import Button from "@imminently/decisively-ui/Button";
import Dialog from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

const GraphStatsButton = () => {
  const { graph, visible } = useGraphVisualisation();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
    >
      <Tooltip>
        <Tooltip.Trigger asChild>
          <Dialog.Trigger asChild>
            <Button
              label={t("debugger.stats")}
              variant="contained"
              intent="secondary"
            >
              <Icon
                name="view-chart"
                size={16}
                className="mr-2"
              />
              {t("debugger.stats")}
            </Button>
          </Dialog.Trigger>
        </Tooltip.Trigger>
        <Tooltip.Content>{t("debugger.stats_tooltip")}</Tooltip.Content>
      </Tooltip>
      <Dialog.Content className={"w-[90rem]"}>
        <DialogHeader title={t("debugger.graph_stats_title")} />
        <Dialog.Body>
          <GraphStatistics
            graph={graph}
            visible={visible}
          />
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};

const useGraphTemporals = () => {
  const { graph, visible, temporalSlice, setTemporalSlice } = useGraphVisualisation();

  const resetSlice = () => {
    setTemporalSlice(null);
  };

  const setSlice = (date) => {
    if (!date) {
      setTemporalSlice(null);
      return;
    }
    // This is to get rid of the timezone - timezone suck! (more than TS)
    const newDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    setTemporalSlice(newDate);
  };

  // The range of any temporal values in the graph (scanned once per graph: a big graph has hundreds of thousands of nodes)
  const { hasTemporal, minTemporal, maxTemporal } = React.useMemo(() => {
    let hasTemporal = false;
    let minTemporal = new Date();
    let maxTemporal = new Date();
    const check = (res: any) => {
      if (!res?.temporal?.ranges) return;
      hasTemporal = true;
      if (res.temporal.ranges.length > 1) {
        const startRange = res.temporal.ranges[0];
        const endRange = last(res.temporal.ranges) as any;
        if (!startRange || !endRange || !startRange.r || !endRange.r) return; // keep processing - bad temporal
        const _minTemporal = new Date(startRange.r.end);
        const _maxTemporal = new Date(endRange.r.start);
        if (_minTemporal < minTemporal) minTemporal = _minTemporal;
        if (_maxTemporal > maxTemporal) maxTemporal = _maxTemporal;
      }
    };
    if (graph) {
      // read the packed values, without making a node object per node
      for (const id of shownIds(graph, visible)) {
        const values = graph.values(id);
        check(values?.input === undefined ? values?.derived : values.input);
      }
    }
    return { hasTemporal, minTemporal, maxTemporal };
  }, [graph, visible]);

  return {
    hasTemporal,
    minTemporal,
    maxTemporal,
    resetSlice,
    setSlice,
    temporalSlice,
  };
};

const TemporalSliceButton = () => {
  const { temporalSlice, hasTemporal, minTemporal, maxTemporal, resetSlice, setSlice } = useGraphTemporals();
  const { t } = useTranslation();

  if (!hasTemporal) return null;

  return (
    <DateTimeInput
      className="w-[12rem]"
      serialize={false}
      value={temporalSlice || null}
      minDate={minTemporal}
      maxDate={maxTemporal}
      onValueChange={setSlice}
      dateOnly
      placeholder={t("debugger.temporal_pick_date")}
      clearable
      onClear={resetSlice}
      clearAriaLabel="Clear date"
    />
  );
};

const FocusAndExpandControls = () => {
  const { debug, graph, restrictedView, showFocusAndExpandControls } = useGraphVisualisation();
  if (!graph) return null;
  const restrictedViewRootPath = restrictedView?.rootPath;
  if (!showFocusAndExpandControls) return null;

  const show = debug && !restrictedViewRootPath ? false : showFocusAndExpandControls;
  if (!show) {
    if (debug) {
      // still show the NodeLimitSelector in debug
      return <NodeLimitSelector />;
    }
    return null;
  }

  return (
    <div className="flex flex-row flex-nowrap justify-start items-center gap-4 z-[5]">
      {debug ? <DepthSelector /> : null}
      {restrictedViewRootPath ? null : <NodeLimitSelector />}
    </div>
  );
};

const GraphToolbar = () => {
  const { debug, graphVizType, extraVizTypes } = useGraphVisualisation();
  const { pathname } = useLocation();
  const activeExtraVizType = extraVizTypes?.find((vizType) => vizType.id === graphVizType);
  const ToolbarComponent = activeExtraVizType?.toolbarComponent;

  return (
    <div className="flex flex-row flex-wrap justify-start items-center gap-4 z-[5] p-4 border-b border-border">
      <GraphTypeSelector />
      {ToolbarComponent ? (
        <ToolbarComponent />
      ) : (
        <>
          <GraphStatsButton />
          <SearchRuleGraphNodes />
          {debug ? <TemporalSliceButton /> : null}
          <FocusAndExpandControls />
        </>
      )}
    </div>
  );
};

export default GraphToolbar;
